import { useState } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import { useNotesApi } from "./useNotesApi"
import { useGetModuleTypesQuery, useGetNoteByIdQuery } from "@app/features/other/notesApiSlice";
import { useHandleProjectState } from "@pages/project/useHandleProjectState";

export const useNotes = () => {
    const [editMode, setEditMode] = useState<boolean>(false);
    const [lastError, setLastError] = useState<string | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const { canMutateNote, isFinalized } = usePermissions();
    const { isFetching: isProjectLoading } = useHandleProjectState();
    const { createNote, updateNote, deleteNote, isLoading, errors } = useNotesApi();
    const { project } = useAppSelector(selectCurrentProject);

    const moduleTypes = useGetModuleTypesQuery({ isContainer: true });
    const projectModuleType = moduleTypes.data?.find((moduleType) => moduleType.name === "Project")?.id;
    
    // Fetch the note by ID if noteId exists
    const { data: noteData, isLoading: isNoteLoading } = useGetNoteByIdQuery({ module_type: projectModuleType ?? 0, project_id: project?.id ?? 0 }, { 
        skip: !projectModuleType || !project?.id
    });
    // Clear error when starting any action
    const clearError = () => setLastError(null);
    // Update error if any of the error messages change
    const currentError = errors.update || errors.create || errors.delete || errors.moduleTypes;
    if (currentError && currentError !== lastError) {
        setLastError(currentError);
    }

    const handleSaveNotes = async (newNote: string) => {
        clearError();
        noteData?.id && newNote ? await updateNote(noteData?.id, newNote) : await createNote(newNote);
        setEditMode(false);
    };

    const confirmDelete = (noteId: number) => {
        setPendingDeleteId(noteId);
    };

    const cancelDelete = () => {
        setPendingDeleteId(null);
    };

    const handleDeleteNote = async () => {
        if (!pendingDeleteId) return;

        clearError();
        await deleteNote(pendingDeleteId);
        setEditMode(false);
        setPendingDeleteId(null);
    };

    return {
        notes: noteData ?? null,
        editMode,
        isLoading: isLoading || isProjectLoading || isNoteLoading,
        error: lastError,
        canMutateNote,
        isFinalized,
        pendingDeleteId,
        handleSaveNotes,
        setEditMode,
        confirmDelete,
        cancelDelete,
        handleDeleteNote,
    };
}; 